<script>
	import { onMount } from 'svelte';
	import ImageService from '$lib/services/images';

	/**
	 * ComponentImage - A responsive image component with WebP support and flexible configuration
	 *
	 * Features:
	 * - Responsive image loading with multiple source sets
	 * - Automatic WebP format conversion for better performance
	 * - SVG detection and WebP bypass for vector graphics
	 * - Flexible configuration for picture and img elements
	 * - Support for all standard HTML img attributes
	 * - Lazy loading and decoding optimization
	 * @example
	 * // Basic usage with responsive sources
	 * <ComponentImage
	 *	sources={content.image.sources}
	 *	imgConfig={{
	 *		alt: content.image.alt,
	 *		class:
	 *			'mb-[2rem] w-full',
	 *		loading: 'lazy'
	 *	}}
	 * />
	 *  // In the config
	 * 		image: {
	 *			sources: [{ srcset: '/assets-mkt/routes/home/rewards_v1.png' }],
	 *			alt: 'Rewards Main'
	 *		}
	 */

	/**
	 * @typedef {object} PictureConfig
	 * @property {string} [class]
	 * @property {string} [id]
	 * @property {string} [style]
	 * @property {string} [title]
	 * @property {string} [lang]
	 * @property {string} [dir]
	 * @property {boolean} [hidden]
	 * @property {number} [tabindex]
	 * @property {string} [role]
	 * @property {string} [ariaLabel]
	 */

	/**
	 * @typedef {object} Source
	 * @property {string} [media]
	 * @property {string} srcset
	 * @property {string} [type]
	 * @property {string} [sizes]
	 * @property {string} [width]
	 * @property {string} [height]
	 */

	/**
	 * @typedef {object} ImgConfig
	 * @property {string} [src]
	 * @property {string} [alt]
	 * @property {string} [width]
	 * @property {string} [height]
	 * @property {'lazy'|'eager'|null} [loading]
	 * @property {'sync'|'async'|'auto'|null} [decoding]
	 * @property {string} [sizes]
	 * @property {string} [srcset]
	 * @property {''|'anonymous'|'use-credentials'|null} [crossorigin]
	 * @property {boolean} [ismap]
	 * @property {string} [usemap]
	 * @property {string} [id]
	 * @property {string} [class]
	 * @property {string} [style]
	 * @property {string} [title]
	 * @property {'high'|'low'|'auto'} [fetchpriority]
	 * @property {string} [dataTrackId]
	 */

	/** @type {PictureConfig} */
	export let pictureConfig = {};

	/** @type {Source[]} */
	export let sources = [];

	/** @type {ImgConfig} */
	export let imgConfig = { alt: '' };

	export let useWebp = true;

	onMount(() => {
		if (sources.length === 0 || !sources[0].srcset) return;

		// Check if we should use WebP
		if (sources.length > 0) {
			const lastSource = sources[sources.length - 1];
			useWebp = useWebp && !ImageService.isSvgImage(lastSource.srcset);
		}

		// Add WebP sources if needed
		if (useWebp) {
			sources = ImageService.createWebPSources(sources);
		}
	});
</script>

<picture {...pictureConfig}>
	{#each sources as source}
		<source {...source} />
	{/each}
	<!-- svelte-ignore a11y-missing-attribute -->
	<img
		{...imgConfig}
		src={sources[sources.length - 1].srcset}
	/>
</picture>
